/**
 * Yoga Storage Utility
 * Manages storing and retrieving yogas from Supabase
 */

import { supabase } from './supabaseClient';
import type { FeedItem } from '../astro/feeds/types';
import { getCachedData, setCachedData } from './permanentDataCache';

export type YogaCategory = 'permanent' | 'time_sensitive' | 'prediction';
export type YogaType = 'benefic' | 'malefic' | 'neutral' | 'mixed';
export type YogaStrength = 'weak' | 'moderate' | 'strong' | 'very_strong';

export interface StoredYoga {
  id?: string;
  user_id: string;
  birth_profile_id?: string | null;
  yoga_key: string;
  yoga_name: string;
  category: YogaCategory;
  is_present: boolean;
  type?: YogaType;
  strength?: YogaStrength;
  title?: string;
  description?: string;
  rule_key?: string;
  template_key?: string;
  life_areas?: string[];
  active_from?: string;
  active_to?: string;
  dasha_context?: {
    MD?: { planet: string; house: number; from?: string; to?: string };
    AD?: { planet: string; house: number; from?: string; to?: string };
    PD?: { planet: string; house: number };
    SD?: { planet: string; house: number };
  };
  planets_involved?: string[];
  houses_involved?: number[];
  score?: number;
  priority?: number;
  chart_snapshot?: any;
  evidence?: string[];
  notes?: string;
  is_private?: boolean;
}

/**
 * Categorize a feed item into yoga category
 */
function categorizeFeedItem(feed: FeedItem): YogaCategory {
  const key = feed.ruleKey || '';
  
  // Permanent natal yogas (have _natal suffix or natal in name)
  if (key.endsWith('_natal') || key.includes('natal')) {
    return 'permanent';
  }
  
  // Check if it's a yoga with key containing 'yoga' - these are permanent
  // Must check BEFORE window check since natal yogas may have window from dasha context
  if (key.includes('yoga') && !key.includes('activated') && !key.startsWith('sade_sati')) {
    return 'permanent';
  }
  
  // Doshas are permanent afflictions
  if (key.includes('dosha') || key.includes('affliction')) {
    return 'permanent';
  }
  
  // Time-sensitive yogas (Sade Sati, activated raj yogas during dasha)
  if (
    key.startsWith('sade_sati') ||
    key.includes('activated') ||
    feed.window // Has timing window
  ) {
    return 'time_sensitive';
  }
  
  // Predictions (planet combinations, house predictions)
  if (
    key.includes('conjunction') ||
    key.includes('aspect') ||
    key.includes('_prediction')
  ) {
    return 'prediction';
  }
  
  return 'prediction';
}

/**
 * Determine yoga type from feed
 */
function determineYogaType(feed: FeedItem): YogaType {
  const score = feed.score || 0;
  const key = feed.ruleKey || '';
  
  // Malefic yogas
  if (
    key.includes('dosha') ||
    key.includes('kaal_sarp') ||
    key.includes('sade_sati') ||
    key.includes('affliction') ||
    score < -20
  ) {
    return 'malefic';
  }
  
  // Benefic yogas
  if (
    key.includes('raj_yoga') ||
    key.includes('mahapurusha') ||
    key.includes('lakshmi') ||
    key.includes('gaja_kesari') ||
    score > 20
  ) {
    return 'benefic';
  }
  
  // Mixed or neutral
  if (score >= -20 && score <= 20) {
    return 'neutral';
  }
  
  return 'neutral';
}

/**
 * Determine yoga strength from score
 */
function determineYogaStrength(score?: number): YogaStrength {
  if (!score) return 'moderate';
  
  const abs = Math.abs(score);
  if (abs >= 60) return 'very_strong';
  if (abs >= 40) return 'strong';
  if (abs >= 20) return 'moderate';
  return 'weak';
}

/**
 * Convert FeedItem to StoredYoga
 */
export function feedItemToStoredYoga(
  feed: FeedItem,
  userId: string,
  birthProfileId?: string | null,
  chartSnapshot?: any
): StoredYoga {
  const category = categorizeFeedItem(feed);
  const type = determineYogaType(feed);
  const strength = determineYogaStrength(feed.score);
  
  // Extract planets and houses from evidence or message
  const planetsInvolved = new Set<string>();
  const housesInvolved = new Set<number>();
  
  // Parse from evidence strings and message
  const textToParse = [...(feed.evidence || []), feed.message].join(' ');
  
  // Look for planet names
  ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'].forEach(planet => {
    if (textToParse.includes(planet)) planetsInvolved.add(planet);
  });
  
  // Look for house numbers (1st, 2nd, etc.)
  const houseMatches = textToParse.match(/(\d+)(?:st|nd|rd|th)\s+house/gi);
  if (houseMatches) {
    houseMatches.forEach(match => {
      const num = parseInt(match);
      if (num >= 1 && num <= 12) housesInvolved.add(num);
    });
  }
  
  // Extract yoga name from message (first line or until colon)
  const yogaName = feed.message.split('\n')[0].split(':')[0].trim();
  
  return {
    user_id: userId,
    birth_profile_id: birthProfileId,
    yoga_key: feed.ruleKey || `custom_${Date.now()}`,
    yoga_name: yogaName || 'Unnamed Yoga',
    category,
    is_present: true, // If it's in feeds, it's present
    type,
    strength,
    title: yogaName,
    description: feed.message,
    rule_key: feed.ruleKey,
    template_key: feed.ruleKey, // Could map to template if needed
    life_areas: [feed.lifeArea], // FeedItem has single lifeArea
    // Fix: For permanent yogas, use epoch date instead of NULL to make UNIQUE constraint work
    active_from: category === 'permanent' ? '1970-01-01T00:00:00Z' : feed.window?.from,
    active_to: category === 'permanent' ? '9999-12-31T23:59:59Z' : feed.window?.to,
    dasha_context: undefined, // Would need more context from feed system
    planets_involved: Array.from(planetsInvolved),
    houses_involved: Array.from(housesInvolved),
    score: feed.score,
    priority: 5, // Default priority
    chart_snapshot: chartSnapshot,
    evidence: feed.evidence || [],
    notes: undefined,
    is_private: feed.private || false,
  };
}

/**
 * Store yogas in Supabase from feed items
 */
export async function storeYogasFromFeeds(
  feeds: FeedItem[],
  userId: string,
  birthProfileId?: string | null,
  chartSnapshot?: any
): Promise<{ success: boolean; stored: number; errors: string[] }> {
  const errors: string[] = [];
  let stored = 0;
  
  try {
    // NULL birth_profile_id means the yoga applies to user's primary/current chart
    // Non-NULL means it's for a saved family member profile (see migration 20251222_revert_yogas_null_handling.sql)
    const normalizedProfileId = birthProfileId || null;
    
    // Convert feeds to stored yogas
    const yogas = feeds.map(feed => 
      feedItemToStoredYoga(feed, userId, normalizedProfileId, chartSnapshot)
    );
    
    // CRITICAL: Deduplicate by yoga_name to avoid PostgreSQL constraint violation
    // Keep the first occurrence of each yoga_name (latest takes precedence if feeds are sorted)
    const uniqueYogas = Array.from(
      new Map(yogas.map(yoga => [yoga.yoga_name, yoga])).values()
    );
    
    if (yogas.length !== uniqueYogas.length) {
      console.warn(`[storeYogasFromFeeds] Deduplicated ${yogas.length - uniqueYogas.length} duplicate yoga names`);
    }
    
    // For NULL profile (primary chart): delete existing yogas then insert new ones
    // For non-NULL profile (family member): use upsert
    if (!normalizedProfileId) {
      // Primary chart: Delete ALL existing yogas (any category) to avoid constraint conflicts
      const { error: deleteError } = await supabase
        .from('user_yogas')
        .delete()
        .eq('user_id', userId)
        .is('birth_profile_id', null);
      // Don't filter by category - remove ALL to ensure clean slate
      
      if (deleteError) {
        errors.push(`Delete error: ${deleteError.message}`);
        console.error('[storeYogasFromFeeds] Delete error:', deleteError);
      }
      
      // Insert fresh yogas
      const { data, error } = await supabase
        .from('user_yogas')
        .insert(uniqueYogas)
        .select();
      
      if (error) {
        errors.push(`Insert error: ${error.message}`);
        console.error('[storeYogasFromFeeds] Insert error:', error);
      } else {
        stored = data?.length || 0;
      }
    } else {
      // Family member profile: Use upsert with (user_id, birth_profile_id, yoga_name)
      const { data, error } = await supabase
        .from('user_yogas')
        .upsert(uniqueYogas, {
          onConflict: 'user_id,birth_profile_id,yoga_name',
          ignoreDuplicates: false
        })
        .select();
      
      if (error) {
        errors.push(`Upsert error: ${error.message}`);
        console.error('[storeYogasFromFeeds] Upsert error:', error);
      } else {
        stored = data?.length || 0;
      }
    }
  } catch (err) {
    errors.push(`Unexpected error: ${err}`);
    console.error('[storeYogasFromFeeds] Exception:', err);
  }
  
  return { success: errors.length === 0, stored, errors };
}

/**
 * Get user's active yogas by category (with 12-hour cache)
 */
export async function getUserActiveYogas(
  userId: string,
  category?: YogaCategory,
  birthProfileId?: string | null
): Promise<StoredYoga[]> {
  try {
    // Generate cache key
    const cacheKey = `yogas_${category || 'all'}_${birthProfileId || 'default'}`;
    
    // Check cache first (12-hour expiry for twice-per-day refresh)
    const cached = getCachedData<StoredYoga[]>(userId, birthProfileId || 'default', cacheKey);
    if (cached) {
      return cached;
    }

    let query = supabase
      .from('user_yogas')
      .select('*')
      .eq('user_id', userId)
      .eq('is_present', true);
    
    if (category) {
      query = query.eq('category', category);
    }
    
    if (birthProfileId) {
      query = query.eq('birth_profile_id', birthProfileId);
    }
    
    // Filter by active time range for time_sensitive yogas
    // Only apply time filtering if NOT already filtering by category=permanent
    if (category !== 'permanent') {
      query = query.or(
        `category.eq.permanent,and(active_from.lte.${new Date().toISOString()},active_to.gte.${new Date().toISOString()})`
      );
    }
    
    const { data, error } = await query
      .order('priority', { ascending: false })
      .order('score', { ascending: false, nullsFirst: false })
      .limit(100); // Limit to top 100 yogas to avoid timeout with duplicate data
    
    if (error) {
      console.error('[getUserActiveYogas] Error:', error);
      return [];
    }

    const result = data || [];
    
    // Cache for 12 hours (twice per day refresh)
    setCachedData(userId, birthProfileId || 'default', cacheKey, result, 'halfday');
    
    return result;
  } catch (err) {
    console.error('[getUserActiveYogas] Exception:', err);
    return [];
  }
}

/**
 * Get permanent yogas only (for Yogas Widget)
 */
export async function getUserPermanentYogas(
  userId: string,
  birthProfileId?: string | null
): Promise<StoredYoga[]> {
  return getUserActiveYogas(userId, 'permanent', birthProfileId);
}

/**
 * Delete all yogas for a user (useful for recalculation)
 */
export async function deleteUserYogas(
  userId: string,
  birthProfileId?: string | null
): Promise<{ success: boolean; deleted: number }> {
  try {
    let query = supabase
      .from('user_yogas')
      .delete()
      .eq('user_id', userId);
    
    if (birthProfileId) {
      query = query.eq('birth_profile_id', birthProfileId);
    }
    
    const { data, error } = await query.select();
    
    if (error) {
      console.error('[deleteUserYogas] Error:', error);
      return { success: false, deleted: 0 };
    }
    
    return { success: true, deleted: data?.length || 0 };
  } catch (err) {
    console.error('[deleteUserYogas] Exception:', err);
    return { success: false, deleted: 0 };
  }
}

/**
 * Update yoga presence (mark as present/absent)
 */
export async function updateYogaPresence(
  yogaId: string,
  isPresent: boolean
): Promise<{ success: boolean }> {
  try {
    const { error } = await supabase
      .from('user_yogas')
      .update({ is_present: isPresent, updated_at: new Date().toISOString() })
      .eq('id', yogaId);
    
    if (error) {
      console.error('[updateYogaPresence] Error:', error);
      return { success: false };
    }
    
    return { success: true };
  } catch (err) {
    console.error('[updateYogaPresence] Exception:', err);
    return { success: false };
  }
}
